import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Checkbox,
  Spin,
  Select,
  Tag,
  Tree,
  Empty,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { ChevronDown, ChevronRight, Search, Check, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  multichoixService,
  multichoixKeys,
} from '../../../services/multichoixService';
import TreeSearchInput from '@client/components/ui/TreeSearchInput';
import axiosInstance from '@client/../services/api';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';
import TutorialButton from '@client/components/ui/TutorialButton';
import { HtmlContent } from '@client/utils/html_content';

// SP-375 (27 mai 26): client confirmed the « Ennemis des cultures » filter
// only lives in Recherche rapide, not Recherche multichoix — the multichoix
// slot was removed from the CMS choices. Extraction still has its own slot.
const MODE_TO_ENEMIES_TUTORIAL_SLOT = {
  extraction: 'treatment-extraction-enemies',
};

const resolveTreatmentMode = (mode) => (mode === 'extraction' ? 'extraction' : null);

// Special value for weeds without a group
const WEED_NO_GROUP_VALUE = '__no_group__';

// SP-430 M88: each weed group gets a distinct colour. There is no colour field
// in the DB, so the mapping lives here (« ceci sera écrit pour le frontend »).
// Keyed by the lowercased group label; falls back to gray for « Autres »/unknown.
const WEED_GROUP_COLORS = {
  'feuilles larges annuelles': '#1677ff',
  'feuilles larges vivaces': '#722ed1',
  'graminées annuelles': '#fa8c16',
  'graminées vivaces': '#13c2c2',
  autres: '#8c8c8c',
};
const weedColorOf = (label) =>
  WEED_GROUP_COLORS[(label || '').toLowerCase().trim()] || '#8c8c8c';

// ============================================================================
// LANGUAGE SELECTOR COMPONENT
// ============================================================================
const LanguageSelector = React.memo(({ value, options, onChange, langLabel }) => (
  <Select
    size="small"
    value={value}
    onChange={onChange}
    options={options}
    aria-label={langLabel}
    className="language-selector"
    // SP-430 M76: show a globe icon + the word « Langues » before the selected
    // language(s), like the culture selector (icône + « Langues » + langues).
    prefix={
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <GlobalOutlined />
        {langLabel ? <span>{langLabel}</span> : null}
      </span>
    }
    optionLabelProp="label"
    style={{ minWidth: 110 }}
  />
));
LanguageSelector.displayName = 'LanguageSelector';

const EnemiesStepContent = ({
  step,
  value,
  selectionLabels,
  allSelections,
  allLabels,
  onChange,
  onLabelsChange,
  onForceRevalidation,
  mode,
}) => {
  const { t } = useTranslation('treatment-search');
  const isMobile = useIsMobile();
  // Restore from stored labels: this component unmounts when the step is inactive.
  const [language, setLanguage] = useState(
    () => (selectionLabels || []).find((l) => l?.language)?.language || 'fr'
  );
  const [expandedKeys, setExpandedKeys] = useState({});
  const [searchValues, setSearchValues] = useState({});
  // State for disease list mode: 'grouping' (par regroupement) or 'organism' (par organisme).
  // SP-430 M83/E81: « par regroupement » is the default.
  const [diseaseListMode, setDiseaseListMode] = useState('grouping');
  // State for weed category filters (multiple selection via checkboxes)
  const [weedCategoryFilters, setWeedCategoryFilters] = useState([]);
  // Mobile sheet state
  const [mobileSheetOpen, setMobileSheetOpen] = useState(null); // null or enemy type string
  const [mobileExpandedNodes, setMobileExpandedNodes] = useState({});

  // Use props directly as source of truth - no local state duplication needed
  // This ensures selections persist when the component unmounts/remounts
  const checkedKeys = value || [];
  const checkedLabels = selectionLabels || [];

  // Transform selections for the API, excluding current step's own selections (enemies)
  // to prevent refetching when the user checks/unchecks an enemy.
  // Step 4's available options only depend on steps 1-3.
  const apiSelections = useMemo(() => {
    const transformed = multichoixService.transformSelectionsForApi(allSelections || {});
    const { enemies, ...previousStepSelections } = transformed;
    return previousStepSelections;
  }, [allSelections]);

  // Fetch enemies from multichoix API (step 4)
  const { data, isLoading, error } = useQuery({
    queryKey: multichoixKeys.stepOptions(4, apiSelections),
    queryFn: () => multichoixService.getStepOptions(4, apiSelections),
    enabled: !!apiSelections.cultures?.length,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch weed groups from API for dynamic checkbox filtering
  const { data: weedGroupsData } = useQuery({
    queryKey: ['weedGroups'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/enemies/weeds-groups/', {
        params: { limit: 100 }, // Fetch all groups
      });
      return response.data.results || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - groups don't change often
  });

  // Memoize weed groups list - filter out "Autres" since we have a dedicated "no group" checkbox
  const weedGroups = useMemo(() => {
    const groups = weedGroupsData || [];
    // Filter out groups named "Autres" (case-insensitive) to avoid duplicate with our "no group" option
    return groups.filter((group) => group.label?.toLowerCase() !== 'autres');
  }, [weedGroupsData]);

  // SP-430 M89/E87: all weed groups are checked by default, so every weed shows
  // up initially; unchecking a group removes its weeds from the list. Initialise
  // once the groups are loaded.
  const weedFiltersInitialized = useRef(false);
  useEffect(() => {
    if (!weedFiltersInitialized.current && weedGroups.length > 0) {
      weedFiltersInitialized.current = true;
      setWeedCategoryFilters([...weedGroups.map((g) => g.id), WEED_NO_GROUP_VALUE]);
    }
  }, [weedGroups]);

  // Get display name based on language.
  // SP-430 (M76-M82/M92): compose the bilingual format expected by the client:
  //   Français (Latin) — « Puceron lanigère du sapin (Adelges piceae) »
  //   Anglais (Latin)  — « Balsam woolly adelgid (Adelges piceae) »
  //   Latin (Français) — « Adelges piceae (Puceron lanigère du sapin) »
  // The latin part keeps disease « asexué / sexual » (latin_display from the API).
  const getDisplayName = useCallback(
    (item, lang = language) => {
      if (!item) return '';
      const french = item.french_name || '';
      const english = item.english_name || '';
      const latin = item.latin_display || item.latin_name || '';
      const compose = (primary, secondary) => {
        if (primary && secondary) return `${primary} (${secondary})`;
        return primary || secondary || '';
      };
      switch (lang) {
        case 'en':
          return compose(english, latin) || item.client_display_name || french || item.label || '';
        case 'latin':
          return compose(latin, french) || french || item.label || '';
        default:
          return compose(french, latin) || item.client_display_name || item.label || '';
      }
    },
    [language]
  );

  // SP-430 M82: the admin rich-text name variants per language mode (latin part
  // keeps its bold/italic markup). Returns the HTML ONLY when it actually
  // contains markup, so plain enemies keep the search-consistent plain title.
  const getDisplayHtml = useCallback(
    (item) => {
      if (!item) return '';
      let html = '';
      if (language === 'en') html = item.display_en_html || '';
      else if (language === 'latin') html = item.display_latin_html || '';
      else html = item.display_fr_html || '';
      return /<[a-zA-Z/]/.test(html) ? html : '';
    },
    [language]
  );

  // Convert API data to Ant Design Tree format
  const convertToTreeData = useCallback(
    (children, enemyType) => {
      if (!children) return [];

      return children.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        const node = {
          key: item.id,
          title: getDisplayName(item),
          isLeaf: !hasChildren,
          selectable: true,
          checkable: true,
          enemyData: item,
          enemyType,
        };

        // SP-430 M91: tag each weed node so its checkbox can be coloured like
        // its group (CSS .weed-leaf-<groupId|autres>, see weedColorStyle).
        if (enemyType === 'weeds') {
          node.className = `weed-leaf-${item.weed_group?.id || 'autres'}`;
        }

        if (hasChildren) {
          node.children = convertToTreeData(item.children, enemyType);
        }

        return node;
      });
    },
    [getDisplayName]
  );

  // Helper to get all descendant keys from a node
  const getAllDescendantKeys = useCallback((node) => {
    const keys = [];
    if (node.children) {
      node.children.forEach((child) => {
        keys.push(child.key);
        keys.push(...getAllDescendantKeys(child));
      });
    }
    return keys;
  }, []);

  // Helper to find a node by key in tree data
  const findNodeByKey = useCallback((nodes, key) => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children) {
        const found = findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Get all keys from tree data
  const getAllKeys = useCallback((nodes) => {
    let keys = [];
    for (const node of nodes) {
      keys.push(node.key);
      if (node.children) {
        keys = keys.concat(getAllKeys(node.children));
      }
    }
    return keys;
  }, []);


  // Filter tree data based on search value
  const filterTreeData = useCallback((nodes, searchValue) => {
    if (!searchValue) return nodes;

    const lowerSearch = searchValue.toLowerCase();

    // SP-430 M74: also match SYNONYMS of the languages shown in the selected
    // mode (fr → Français (Latin) ⇒ french+latin ; en ⇒ english+latin ;
    // latin ⇒ latin+french), so typing an enemy synonym surfaces the enemy.
    const synFieldsByLang = {
      en: ['english_name_synonyms', 'latin_name_synonyms'],
      latin: ['latin_name_synonyms', 'french_name_synonyms'],
      fr: ['french_name_synonyms', 'latin_name_synonyms'],
    };
    const synFields = synFieldsByLang[language] || synFieldsByLang.fr;

    // SP-430 M75: return the SPECIFIC synonym that matched (split the
    // comma-joined synonym strings), so it can be shown in gray next to the
    // enemy name. Only the synonyms of the languages in the selected mode.
    const findMatchedSynonym = (node) => {
      const data = node.enemyData || {};
      for (const field of synFields) {
        const value = data[field];
        if (!value) continue;
        const part = String(value)
          .split(',')
          .map((s) => s.trim())
          .find((s) => s && s.toLowerCase().includes(lowerSearch));
        if (part) return part;
      }
      return null;
    };

    const filterNodes = (nodeList) => {
      return nodeList
        .map((node) => {
          const titleMatch =
            !!node.title && node.title.toLowerCase().includes(lowerSearch);
          // M75: only surface a synonym label when the match is NOT by the
          // visible name (« si un synonyme est tapé »).
          const matchedSynonym = titleMatch ? null : findMatchedSynonym(node);
          const filteredChildren = node.children
            ? filterNodes(node.children)
            : [];

          if (titleMatch || matchedSynonym || filteredChildren.length > 0) {
            return {
              ...node,
              matchedSynonym: matchedSynonym || undefined,
              children:
                filteredChildren.length > 0 ? filteredChildren : node.children,
            };
          }
          return null;
        })
        .filter(Boolean);
    };

    return filterNodes(nodes);
  }, [language]);



  // Filter enemies by weed group (using weed_group.id)
  // Works recursively on the tree structure
  // Both parent and child nodes can have weed_group assigned
  const filterByWeedCategory = useCallback((enemies, selectedGroupIds) => {
    if (!selectedGroupIds || selectedGroupIds.length === 0) {
      return enemies; // No filter applied, return all
    }

    // Helper to check if an enemy matches the filter (parent or leaf)
    const matchesFilter = (enemy) => {
      const weedGroup = enemy.weed_group;
      if (!weedGroup || !weedGroup.id) {
        // Include enemies without weed_group only if 'no group' option is selected
        return selectedGroupIds.includes(WEED_NO_GROUP_VALUE);
      }
      return selectedGroupIds.includes(weedGroup.id);
    };

    // Recursively filter the tree
    const filterTree = (nodes) => {
      return nodes
        .map((node) => {
          // Check if this node itself matches the filter
          const nodeMatches = matchesFilter(node);

          // If node has children, filter them recursively
          if (node.children && node.children.length > 0) {
            const filteredChildren = filterTree(node.children);

            // Keep parent if:
            // 1. The parent node itself matches the filter, OR
            // 2. It has children that match the filter
            if (nodeMatches || filteredChildren.length > 0) {
              return {
                ...node,
                // If parent matches, keep all children; otherwise only matching children
                children: nodeMatches ? node.children : filteredChildren,
              };
            }
            // Neither parent nor children match - remove
            return null;
          }

          // Leaf node - check if it matches the filter
          return nodeMatches ? node : null;
        })
        .filter(Boolean); // Remove null entries
    };

    return filterTree(enemies);
  }, []);

  // Handle tree check with custom behavior
  const handleCheck = useCallback(
    (_checkedKeysValue, info, enemyType, treeData) => {
      const { node, checked } = info;

      // Get all keys in current tree to determine which IDs belong to this enemy type
      const allKeysInCurrentTree = getAllKeys(treeData);

      // Get current checked keys for this enemy type
      // Use tree data membership as fallback when labels don't have enemyType (cold start)
      const currentTypeKeys = checkedKeys.filter((id) => {
        const label = checkedLabels.find((l) => l.value === id);
        if (label && label.enemyType) {
          return label.enemyType === enemyType;
        }
        // Cold start fallback: check if ID exists in this tree
        return allKeysInCurrentTree.includes(id);
      });

      let newTypeKeys = [...currentTypeKeys];

      if (checked) {
        // When checking a node, add it and all its descendants
        newTypeKeys.push(node.key);
        const descendantKeys = getAllDescendantKeys(node);
        descendantKeys.forEach((key) => {
          if (!newTypeKeys.includes(key)) {
            newTypeKeys.push(key);
          }
        });
      } else {
        // When unchecking a node, only remove that specific node (not the parent)
        newTypeKeys = newTypeKeys.filter((key) => key !== node.key);
        // Also uncheck all descendants if unchecking a parent
        const descendantKeys = getAllDescendantKeys(node);
        newTypeKeys = newTypeKeys.filter(
          (key) => !descendantKeys.includes(key)
        );
      }

      // Remove duplicates
      newTypeKeys = [...new Set(newTypeKeys)];

      // Get all currently checked items across other enemy types
      // Use tree data membership as fallback when labels don't have enemyType (cold start)
      const otherTypeItems = checkedLabels.filter((item) => {
        if (item.enemyType) {
          return item.enemyType !== enemyType;
        }
        // Cold start fallback: exclude items that exist in current tree
        return !allKeysInCurrentTree.includes(item.value);
      });
      const otherTypeIds = checkedKeys.filter((id) => {
        const existingLabel = checkedLabels.find((l) => l.value === id);
        if (existingLabel && existingLabel.enemyType) {
          return existingLabel.enemyType !== enemyType;
        }
        // Cold start fallback: exclude IDs that exist in current tree
        return !allKeysInCurrentTree.includes(id);
      });

      // Build labels for the new type keys
      const newTypeItems = newTypeKeys.map((key) => {
        const foundNode = findNodeByKey(treeData, key);
        return {
          value: key,
          label: foundNode ? getDisplayName(foundNode.enemyData) : key,
          enemyType,
          language,
        };
      });

      // Combine with other types
      const allIds = [...otherTypeIds, ...newTypeKeys];
      const allLabelsArr = [...otherTypeItems, ...newTypeItems];

      // Only call onChange - parent state is source of truth
      onChange(allIds, allLabelsArr);
    },
    [
      checkedKeys,
      checkedLabels,
      getDisplayName,
      language,
      onChange,
      getAllDescendantKeys,
      findNodeByKey,
      getAllKeys,
    ]
  );

  // Handle select all for a specific enemy type
  const handleSelectAll = useCallback(
    (enemyType, treeData, checked) => {
      const allTypeKeys = getAllKeys(treeData);

      // Get current checked items for other types
      // Use tree data membership as fallback when labels don't have enemyType (cold start)
      const otherTypeItems = checkedLabels.filter((item) => {
        if (item.enemyType) {
          return item.enemyType !== enemyType;
        }
        // Cold start fallback: exclude items that exist in current tree
        return !allTypeKeys.includes(item.value);
      });
      const otherTypeIds = checkedKeys.filter((id) => {
        const existingLabel = checkedLabels.find((l) => l.value === id);
        if (existingLabel && existingLabel.enemyType) {
          return existingLabel.enemyType !== enemyType;
        }
        // Cold start fallback: exclude IDs that exist in current tree
        return !allTypeKeys.includes(id);
      });

      if (checked) {
        // Select all items in this type
        const newTypeItems = allTypeKeys.map((key) => {
          const foundNode = findNodeByKey(treeData, key);
          return {
            value: key,
            label: foundNode ? getDisplayName(foundNode.enemyData) : key,
            enemyType,
            language,
          };
        });

        const allIds = [...otherTypeIds, ...allTypeKeys];
        const allLabelsArr = [...otherTypeItems, ...newTypeItems];

        onChange(allIds, allLabelsArr);
      } else {
        // Deselect all items in this type
        onChange(otherTypeIds, otherTypeItems);
      }
    },
    [
      checkedKeys,
      checkedLabels,
      getAllKeys,
      findNodeByKey,
      getDisplayName,
      language,
      onChange,
    ]
  );

  // SP-430 M90: select / deselect ALL weeds of one weed group at once. The keys
  // are the selectable weed leaves whose weed_group matches the group (or have
  // no group for « Autres »), taken from the current (filtered) weed tree.
  const getWeedGroupKeys = useCallback((treeData, groupId) => {
    const keys = [];
    const walk = (nodes) => {
      (nodes || []).forEach((n) => {
        const wg = n.enemyData?.weed_group;
        const inGroup =
          groupId === WEED_NO_GROUP_VALUE
            ? !(wg && wg.id)
            : !!(wg && wg.id === groupId);
        // A weed carrying a group is selectable whether or not it has children
        // (e.g. « Arbres et arbustes » is itself a weed AND a parent), so do NOT
        // restrict to leaves — that was missing parent-weeds (SP-430 M90).
        if (inGroup && n.selectable !== false) keys.push(n.key);
        if (n.children) walk(n.children);
      });
    };
    walk(treeData);
    return keys;
  }, []);

  const isWeedGroupAllSelected = useCallback(
    (treeData, groupId) => {
      const keys = getWeedGroupKeys(treeData, groupId);
      return keys.length > 0 && keys.every((k) => checkedKeys.includes(k));
    },
    [getWeedGroupKeys, checkedKeys]
  );

  const handleSelectWeedGroup = useCallback(
    (enemyType, treeData, groupId, checked) => {
      const groupKeys = getWeedGroupKeys(treeData, groupId);
      if (checked) {
        const additions = groupKeys
          .filter((k) => !checkedKeys.includes(k))
          .map((k) => {
            const node = findNodeByKey(treeData, k);
            return {
              value: k,
              label: node ? getDisplayName(node.enemyData) : k,
              enemyType,
              language,
            };
          });
        onChange(
          [...checkedKeys, ...additions.map((a) => a.value)],
          [...checkedLabels, ...additions]
        );
      } else {
        onChange(
          checkedKeys.filter((k) => !groupKeys.includes(k)),
          checkedLabels.filter((l) => !groupKeys.includes(l.value))
        );
      }
    },
    [getWeedGroupKeys, checkedKeys, checkedLabels, findNodeByKey, getDisplayName, language, onChange]
  );

  // Handle expand
  const handleExpand = useCallback((keys, enemyType) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [enemyType]: keys,
    }));
  }, []);

  // Handle remove tag
  const handleRemoveTag = useCallback(
    (itemToRemove) => {
      const newKeys = checkedKeys.filter((key) => key !== itemToRemove.value);
      const newLabels = checkedLabels.filter(
        (item) => item.value !== itemToRemove.value
      );

      onChange(newKeys, newLabels);
    },
    [checkedKeys, checkedLabels, onChange]
  );

  // Handle weed category checkbox change
  const handleWeedCategoryChange = useCallback((category, checked) => {
    setWeedCategoryFilters((prev) => {
      if (checked) {
        return [...prev, category];
      }
      return prev.filter((c) => c !== category);
    });
  }, []);

  // Get checked keys for a specific enemy type
  // Handles both interactive selection (label has enemyType) and cold start restoration (no enemyType)
  const getCheckedKeysForType = useCallback(
    (enemyType, treeData) => {
      const allKeysInTree = getAllKeys(treeData);
      return checkedKeys.filter((id) => {
        const label = checkedLabels.find((l) => l.value === id);
        // If label has enemyType, use it for filtering
        if (label && label.enemyType) {
          return label.enemyType === enemyType;
        }
        // Cold start fallback: check if ID exists in this enemy type's tree data
        return allKeysInTree.includes(id);
      });
    },
    [checkedKeys, checkedLabels, getAllKeys]
  );

  // Check if all items are selected for a type
  const isAllSelectedForType = useCallback(
    (enemyType, treeData) => {
      const allKeys = getAllKeys(treeData);
      const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
      return (
        allKeys.length > 0 &&
        allKeys.every((key) => typeCheckedKeys.includes(key))
      );
    },
    [getAllKeys, getCheckedKeysForType]
  );

  // Enemy type labels
  const enemyTypeLabels = {
    pest: t('multichoix.steps.enemies.pests', 'RAVAGEURS'),
    disease: t('multichoix.steps.enemies.diseases', 'MALADIES'),
    weeds: t('multichoix.steps.enemies.weeds', 'MAUVAISES HERBES'),
  };

  // Language options for the selector.
  // SP-430 M77/M78: each option composes a primary + latin/french pair; the
  // default selected language is « Français (Latin) ».
  const languageOptions = useMemo(
    () => [
      { value: 'fr', label: 'Français (Latin)' },
      { value: 'en', label: 'Anglais (Latin)' },
      { value: 'latin', label: 'Latin (Français)' },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" />
        <span className="ml-3">
          {t('multichoix.common.loading', 'Chargement...')}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t('multichoix.errors.load_failed', 'Erreur de chargement')}
        description={error.message}
        showIcon
      />
    );
  }

  const enemyTypes = data?.options || [];

  if (enemyTypes.length === 0) {
    return (
      <Empty
        description={t(
          'multichoix.steps.enemies.no_enemies',
          'Aucun ennemi disponible pour les filtres sélectionnés'
        )}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // IS-26922: relabel the stored selection so labels (used by the Excel
  // extraction header) follow the « Langues » selector. Labels only: going
  // through onChange would cascade-reset the later steps.
  const handleLanguageChange = (val) => {
    setLanguage(val);
    const trees = enemyTypes.flatMap((et) =>
      [et.children, et.tree_by_grouping, et.tree_by_hierarchy].map((src) =>
        convertToTreeData(src || [], et.enemy_type)
      )
    );
    onLabelsChange?.(
      (selectionLabels || []).map((l) => {
        const node = trees.map((tree) => findNodeByKey(tree, l.value)).find(Boolean);
        // Not found: keep the old label and its old language (not recomposed).
        return node
          ? { ...l, language: val, label: getDisplayName(node.enemyData, val) }
          : l;
      })
    );
    onForceRevalidation?.();
  };

  // Mobile tree node renderer
  const renderMobileTreeNode = (node, enemyType, treeData, level = 0) => {
    const isExpanded = mobileExpandedNodes[`${enemyType}-${node.key}`];
    const hasChildren = node.children && node.children.length > 0;
    const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
    const isChecked = typeCheckedKeys.includes(node.key);
    // SP-430 M91: weed item checkboxes take their group's colour.
    const weedColor =
      enemyType === 'weeds' ? weedColorOf(node.enemyData?.weed_group?.label) : null;

    return (
      <div key={node.key}>
        <div
          className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${
            isChecked ? 'bg-green-50' : ''
          }`}
          style={{ paddingLeft: `${12 + level * 16}px` }}
          onClick={() => {
            if (hasChildren) {
              setMobileExpandedNodes((prev) => ({
                ...prev,
                [`${enemyType}-${node.key}`]: !isExpanded,
              }));
            }
            // Toggle check for this node
            handleCheck(
              null,
              { node, checked: !isChecked },
              enemyType,
              treeData
            );
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown size={16} className="mr-2 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRight size={16} className="mr-2 text-gray-500 flex-shrink-0" />
            )
          ) : (
            <div className="w-4 mr-2" />
          )}
          <div
            className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
              weedColor
                ? ''
                : isChecked
                ? 'bg-[#207d54] border-[#207d54]'
                : 'border-gray-300'
            }`}
            style={
              weedColor
                ? {
                    backgroundColor: isChecked ? weedColor : 'transparent',
                    borderColor: weedColor,
                  }
                : undefined
            }
          >
            {isChecked && <Check size={14} className="text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs ${hasChildren ? 'font-medium text-[#207d54]' : 'text-gray-900'}`}>
              {/* SP-430 M82: admin rich-text name (HtmlContent) when markup, else plain title. */}
              {getDisplayHtml(node.enemyData) ? (
                <HtmlContent html={getDisplayHtml(node.enemyData)} as="span" />
              ) : (
                node.title
              )}
              {/* SP-430 M75: matched synonym shown in gray. */}
              {node.matchedSynonym && (
                <span className="text-gray-400 italic ml-2">
                  Synonyme&nbsp;: {node.matchedSynonym}
                </span>
              )}
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child) =>
              renderMobileTreeNode(child, enemyType, treeData, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // Mobile render
  if (isMobile) {
    return (
      <div className="space-y-3">
        {/* Language selector - compact mobile version (M76: globe + « Langues »
            + selected language, now carried by the selector prefix itself). */}
        <div className="flex items-center justify-end px-1">
          <LanguageSelector
            value={language}
            options={languageOptions}
            langLabel={t('multichoix.steps.enemies.languages', 'Langues')}
            onChange={handleLanguageChange}
          />
        </div>

        {/* Enemy type cards */}
        {enemyTypes.map((enemyTypeData) => {
          const enemyType = enemyTypeData.enemy_type;
          const flatEnemies = enemyTypeData.children || [];

          // SP-430 M88: weed groups present in the current list (mobile).
          let visibleWeedGroups = [];
          let hasUngroupedWeeds = false;
          if (enemyType === 'weeds') {
            const presentWeedGroupIds = new Set();
            const collectWeedGroups = (list) => {
              (list || []).forEach((e) => {
                const wg = e.weed_group;
                if (wg && wg.id) presentWeedGroupIds.add(wg.id);
                else hasUngroupedWeeds = true;
                if (e.children) collectWeedGroups(e.children);
              });
            };
            collectWeedGroups(flatEnemies);
            visibleWeedGroups = weedGroups.filter((g) => presentWeedGroupIds.has(g.id));
          }

          let filteredEnemies = flatEnemies;
          if (enemyType === 'weeds' && weedCategoryFilters.length > 0) {
            filteredEnemies = filterByWeedCategory(flatEnemies, weedCategoryFilters);
          }

          let treeData;
          if (enemyType === 'disease') {
            if (diseaseListMode === 'grouping') {
              treeData = convertToTreeData(enemyTypeData.tree_by_grouping || [], enemyType);
            } else {
              treeData = convertToTreeData(enemyTypeData.tree_by_hierarchy || [], enemyType);
            }
          } else {
            treeData = convertToTreeData(filteredEnemies, enemyType);
          }

          const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
          const searchValue = searchValues[enemyType] || '';
          const filteredTreeData = filterTreeData(treeData, searchValue);

          return (
            <div key={enemyType} className="w-full">
              {/* Mobile trigger */}
              <div
                onClick={() => setMobileSheetOpen(enemyType)}
                className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900">
                    {enemyTypeLabels[enemyType] || enemyTypeData.label}
                  </div>
                  {typeCheckedKeys.length > 0 && (
                    <div className="text-xs text-[#207d54] mt-0.5">
                      {typeCheckedKeys.length} {t('multichoix.common.selected', 'sélectionné(s)')}
                    </div>
                  )}
                </div>
                <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
              </div>

              {/* Mobile sheet for this enemy type */}
              <MobileSheetBase
                isOpen={mobileSheetOpen === enemyType}
                onClose={() => setMobileSheetOpen(null)}
                title={enemyTypeLabels[enemyType] || enemyTypeData.label}
                showDone
                onDone={() => setMobileSheetOpen(null)}
              >
                {/* Search */}
                <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                      placeholder={t('multichoix.steps.enemies.search_placeholder', 'Rechercher...')}
                      value={searchValue}
                      onChange={(e) =>
                        setSearchValues((prev) => ({
                          ...prev,
                          [enemyType]: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Filters for diseases — SP-430 M83: subtitle + « par
                    regroupement » (default) FIRST then « par organisme », with
                    the disease-specific labels, matching the desktop. */}
                {enemyType === 'disease' && (
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <div className="text-xs font-medium text-gray-700 mb-2">
                      {t('multichoix.steps.enemies.disease_display_title', "Choix du type d'affichage des maladies")}
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`diseaseMode-${enemyType}`}
                          value="grouping"
                          checked={diseaseListMode === 'grouping'}
                          onChange={(e) => setDiseaseListMode(e.target.value)}
                          className="w-4 h-4 accent-[#207d54]"
                        />
                        <span className="text-xs">
                          {t('multichoix.steps.enemies.disease_by_grouping', 'Liste de maladies par regroupement')}
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`diseaseMode-${enemyType}`}
                          value="organism"
                          checked={diseaseListMode === 'organism'}
                          onChange={(e) => setDiseaseListMode(e.target.value)}
                          className="w-4 h-4 accent-[#207d54]"
                        />
                        <span className="text-xs">
                          {t('multichoix.steps.enemies.disease_by_organism', 'Liste de maladies par organisme')}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Weed category filters — SP-430 M88: subtitle + only groups
                    with ≥1 weed + a distinct colour per group (native checkbox
                    accentColor). */}
                {enemyType === 'weeds' && visibleWeedGroups.length > 0 && (
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <div className="text-xs font-medium text-gray-700 mb-2">
                      {t('multichoix.steps.enemies.weed_groups_title', 'Groupes de mauvaises herbes à afficher')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {visibleWeedGroups.map((group) => (
                        <label key={group.id} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={weedCategoryFilters.includes(group.id)}
                            onChange={(e) => handleWeedCategoryChange(group.id, e.target.checked)}
                            className="w-4 h-4"
                            style={{ accentColor: weedColorOf(group.label) }}
                          />
                          <span className="text-xs">{group.label}</span>
                        </label>
                      ))}
                      {hasUngroupedWeeds && (
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={weedCategoryFilters.includes(WEED_NO_GROUP_VALUE)}
                            onChange={(e) => handleWeedCategoryChange(WEED_NO_GROUP_VALUE, e.target.checked)}
                            className="w-4 h-4"
                            style={{ accentColor: weedColorOf('Autres') }}
                          />
                          <span className="text-xs">{t('multichoix.steps.enemies.weeds.other', 'Autres')}</span>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* Select all */}
                {/* SP-430 M69/E69: « Sélectionner tout » must act on the
                    SEARCH-FILTERED list (filteredTreeData), not the full tree —
                    mirrors the desktop branch. Otherwise a search that narrows
                    30 enemies to 15 would still select all 30. */}
                <div className="px-3 py-2 border-b border-gray-100">
                  <div
                    className="flex items-center min-h-[40px] cursor-pointer"
                    onClick={() => handleSelectAll(enemyType, filteredTreeData, !isAllSelectedForType(enemyType, filteredTreeData))}
                  >
                    <div
                      className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                        isAllSelectedForType(enemyType, filteredTreeData) ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                      }`}
                    >
                      {isAllSelectedForType(enemyType, filteredTreeData) && <Check size={14} className="text-white" />}
                    </div>
                    <span className="text-xs font-medium">
                      {t('multichoix.common.select_all', 'Sélectionner tout')}
                    </span>
                  </div>
                  {/* SP-430 M90: per-group « Sélectionner toutes les … »,
                      indented, coloured like their group. */}
                  {enemyType === 'weeds' && visibleWeedGroups.length > 0 && (
                    <div className="ml-7 mt-1 flex flex-col gap-1">
                      {[
                        ...visibleWeedGroups,
                        ...(hasUngroupedWeeds ? [{ id: WEED_NO_GROUP_VALUE, label: 'Autres' }] : []),
                      ].map((g) => {
                        const groupChecked = isWeedGroupAllSelected(filteredTreeData, g.id);
                        const groupColor = weedColorOf(g.label);
                        return (
                          <div
                            key={g.id}
                            className="flex items-center min-h-[36px] cursor-pointer"
                            onClick={() =>
                              handleSelectWeedGroup(enemyType, filteredTreeData, g.id, !groupChecked)
                            }
                          >
                            <div
                              className="w-4 h-4 border-2 rounded mr-2 flex-shrink-0 flex items-center justify-center"
                              style={{
                                backgroundColor: groupChecked ? groupColor : 'transparent',
                                borderColor: groupColor,
                              }}
                            >
                              {groupChecked && <Check size={12} className="text-white" />}
                            </div>
                            <span className="text-xs">
                              {g.id === WEED_NO_GROUP_VALUE
                                ? 'Sélectionner toutes les autres mauvaises herbes'
                                : `Sélectionner toutes les ${(g.label || '').toLowerCase()}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Tree options */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                  {filteredTreeData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                      <div className="text-xs">{t('multichoix.steps.enemies.no_results', 'Aucun ennemi trouvé')}</div>
                    </div>
                  ) : (
                    <div className="py-1">
                      {filteredTreeData.map((node) =>
                        renderMobileTreeNode(node, enemyType, treeData, 0)
                      )}
                    </div>
                  )}
                </div>

                {/* Selected tags at bottom */}
                {typeCheckedKeys.length > 0 && (
                  <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="text-xs text-gray-500 mb-2">
                      {t('multichoix.steps.enemies.selected_elements', 'Éléments sélectionnés:')}
                    </div>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {typeCheckedKeys.slice(0, 10).map((key) => {
                        const label = checkedLabels.find((l) => l.value === key);
                        const node = findNodeByKey(treeData, key);
                        // SP-430 M79: recompute the tag label LIVE from the
                        // current-language tree so an already-selected enemy
                        // switches language with the « Langues » dropdown; fall
                        // back to the stored label only if the node is gone.
                        const displayLabel = node ? getDisplayName(node.enemyData) : (label?.label || key);
                        return (
                          <Tag
                            key={key}
                            closable
                            onClose={(e) => {
                              e.preventDefault();
                              handleRemoveTag({ value: key });
                            }}
                            className="bg-[#5b8c00] border-[#5b8c00] text-white text-xs px-1.5 py-0.5"
                          >
                            {displayLabel}
                          </Tag>
                        );
                      })}
                      {typeCheckedKeys.length > 10 && (
                        <Tag className="bg-gray-200 text-gray-600 text-xs px-1.5 py-0.5">
                          +{typeCheckedKeys.length - 10}
                        </Tag>
                      )}
                    </div>
                  </div>
                )}
              </MobileSheetBase>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop render
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <TutorialButton slotKey={MODE_TO_ENEMIES_TUTORIAL_SLOT[resolveTreatmentMode(mode)]} />
      </div>
      {enemyTypes.map((enemyTypeData) => {
        const enemyType = enemyTypeData.enemy_type;

        // Get flat enemy list from children
        const flatEnemies = enemyTypeData.children || [];

        // SP-430 M88: only show weed-group checkboxes for groups that actually
        // have ≥1 weed in the current list, and build the per-group colour CSS.
        let visibleWeedGroups = [];
        let hasUngroupedWeeds = false;
        let weedColorStyle = '';
        if (enemyType === 'weeds') {
          const presentWeedGroupIds = new Set();
          const collectWeedGroups = (list) => {
            (list || []).forEach((e) => {
              const wg = e.weed_group;
              if (wg && wg.id) presentWeedGroupIds.add(wg.id);
              else hasUngroupedWeeds = true;
              if (e.children) collectWeedGroups(e.children);
            });
          };
          collectWeedGroups(flatEnemies);
          visibleWeedGroups = weedGroups.filter((g) => presentWeedGroupIds.has(g.id));
          weedColorStyle = [
            // filterKey = the group-filter / M90 select-all checkbox class;
            // leafKey = the tree-node class set in convertToTreeData (M91).
            ...visibleWeedGroups.map((g) => ({
              filterKey: g.id,
              leafKey: g.id,
              color: weedColorOf(g.label),
            })),
            {
              filterKey: WEED_NO_GROUP_VALUE,
              leafKey: 'autres',
              color: weedColorOf('Autres'),
            },
          ]
            .map(({ filterKey, leafKey, color }) => {
              // The .green-checkbox SCSS forces green on hover, incl. a
              // (0,5,0) rule that sets the FILL of a checked box on hover:
              //   .green-checkbox .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner
              // We compound .weed-cb-X with .ant-checkbox-wrapper (same element)
              // to reach (0,6,0) and win in EVERY state (border, fill, hover).
              const cb = `.green-checkbox .weed-cb-${filterKey}.ant-checkbox-wrapper`;
              const leaf = `.enemy-tree .weed-leaf-${leafKey}`;
              return (
                // M88/M90 group-filter + per-group select-all boxes:
                `${cb} .ant-checkbox-inner{border-color:${color}!important;}\n` +
                `${cb} .ant-checkbox-checked .ant-checkbox-inner{background-color:${color}!important;border-color:${color}!important;}\n` +
                `${cb}:hover .ant-checkbox-inner{border-color:${color}!important;}\n` +
                `${cb}:hover .ant-checkbox-checked .ant-checkbox-inner{background-color:${color}!important;border-color:${color}!important;}\n` +
                `${cb} .ant-checkbox:hover .ant-checkbox-inner{border-color:${color}!important;}\n` +
                // M91 each weed item's checkbox in the list:
                `${leaf} .ant-tree-checkbox-inner{border-color:${color}!important;}\n` +
                `${leaf} .ant-tree-checkbox-checked .ant-tree-checkbox-inner{background-color:${color}!important;border-color:${color}!important;}\n` +
                `${leaf} .ant-tree-checkbox:hover .ant-tree-checkbox-inner{border-color:${color}!important;}\n` +
                `${leaf}:hover .ant-tree-checkbox-checked .ant-tree-checkbox-inner{background-color:${color}!important;border-color:${color}!important;}`
              );
            })
            .join('\n');
        }

        // Apply weed category filter for weeds
        let filteredEnemies = flatEnemies;
        if (enemyType === 'weeds' && weedCategoryFilters.length > 0) {
          filteredEnemies = filterByWeedCategory(
            flatEnemies,
            weedCategoryFilters
          );
        }

        // Build tree based on enemy type and list mode
        let treeData;
        if (enemyType === 'disease') {
          // Use pre-built trees from API based on selected mode
          if (diseaseListMode === 'grouping') {
            treeData = convertToTreeData(enemyTypeData.tree_by_grouping || [], enemyType);
          } else {
            treeData = convertToTreeData(enemyTypeData.tree_by_hierarchy || [], enemyType);
          }
        } else {
          // For pests and weeds, use the default tree conversion
          treeData = convertToTreeData(filteredEnemies, enemyType);
        }
        const typeCheckedKeys = getCheckedKeysForType(enemyType, treeData);
        const searchValue = searchValues[enemyType] || '';
        const filteredTreeData = filterTreeData(treeData, searchValue);
        // Build labels for selected items - use existing labels if they have enemyType, otherwise build from tree
        const typeSelectedLabels = typeCheckedKeys.map((id) => {
          const existingLabel = checkedLabels.find((l) => l.value === id);
          if (existingLabel && existingLabel.enemyType === enemyType) {
            return existingLabel;
          }
          // Cold start fallback: build label from tree data
          const node = findNodeByKey(treeData, id);
          return {
            value: id,
            label: node ? getDisplayName(node.enemyData) : id,
            enemyType,
            language,
          };
        });
        // SP-430 M69/E69: « Sélectionner tout » and its checked state reflect the
        // currently SEARCH-FILTERED enemies, not the whole list.
        const isAllSelected = isAllSelectedForType(enemyType, filteredTreeData);

        return (
          <div key={enemyType} className="rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-800">
                    {enemyTypeLabels[enemyType] || enemyTypeData.label}
                  </span>
                  <Tooltip
                    title={t(
                      'multichoix.steps.enemies.tooltip',
                      'Sélectionnez les ennemis à cibler'
                    )}
                  >
                    <InfoCircleOutlined className="text-gray-500" />
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Content - 50/50 layout */}
            <div className="flex flex-col lg:flex-row">
              {/* Left side - Tree (50%) */}
              <div className="flex-1 p-4 green-checkbox">
                {/* SP-430 M83/E81: for diseases, the display-mode radios (with
                    their subtitle) come ABOVE the search bar; « par regroupement »
                    is listed first and is the default. */}
                {enemyType === 'disease' && (
                  <div className="mb-3">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {t(
                        'multichoix.steps.enemies.disease_display_title',
                        "Choix du type d'affichage des maladies"
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="diseaseListMode"
                          value="grouping"
                          checked={diseaseListMode === 'grouping'}
                          onChange={(e) => setDiseaseListMode(e.target.value)}
                          className="w-4 h-4 accent-[#5b8c00]"
                        />
                        <span className="text-sm">
                          {t('multichoix.steps.enemies.disease_by_grouping', 'Liste de maladies par regroupement')}
                        </span>
                        <Tooltip title={t('multichoix.steps.enemies.disease_by_grouping_help', 'Regroupe les maladies sous leur fiche de regroupement.')}>
                          <InfoCircleOutlined className="text-gray-500" />
                        </Tooltip>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="diseaseListMode"
                          value="organism"
                          checked={diseaseListMode === 'organism'}
                          onChange={(e) => setDiseaseListMode(e.target.value)}
                          className="w-4 h-4 accent-[#5b8c00]"
                        />
                        <span className="text-sm">
                          {t('multichoix.steps.enemies.disease_by_organism', 'Liste de maladies par organisme')}
                        </span>
                        <Tooltip title={t('multichoix.steps.enemies.disease_by_organism_help', 'Liste les maladies par organisme responsable.')}>
                          <InfoCircleOutlined className="text-gray-500" />
                        </Tooltip>
                      </label>
                    </div>
                  </div>
                )}

                {/* SP-430 M88: for weeds, the « Groupes de mauvaises herbes à
                    afficher » subtitle + the colored group checkboxes come
                    ABOVE the search bar. Only groups that actually have ≥1 weed
                    in the current list are shown; each group has a distinct
                    colour (weedColorStyle), default all checked (M89). */}
                {enemyType === 'weeds' && visibleWeedGroups.length > 0 && (
                  <div className="mb-3 green-checkbox">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {t(
                        'multichoix.steps.enemies.weed_groups_title',
                        'Groupes de mauvaises herbes à afficher'
                      )}
                    </div>
                    <style>{weedColorStyle}</style>
                    <div className="flex flex-wrap gap-3">
                      {visibleWeedGroups.map((group) => (
                        <Checkbox
                          key={group.id}
                          className={`weed-cb-${group.id}`}
                          checked={weedCategoryFilters.includes(group.id)}
                          onChange={(e) =>
                            handleWeedCategoryChange(group.id, e.target.checked)
                          }
                        >
                          {group.label}
                        </Checkbox>
                      ))}
                      {hasUngroupedWeeds && (
                        <Checkbox
                          key={WEED_NO_GROUP_VALUE}
                          className={`weed-cb-${WEED_NO_GROUP_VALUE}`}
                          checked={weedCategoryFilters.includes(WEED_NO_GROUP_VALUE)}
                          onChange={(e) =>
                            handleWeedCategoryChange(WEED_NO_GROUP_VALUE, e.target.checked)
                          }
                        >
                          {t('multichoix.steps.enemies.weeds.other', 'Autres')}
                        </Checkbox>
                      )}
                    </div>
                  </div>
                )}

                {/* Label and Language row */}
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-600">
                    {t(
                      'multichoix.steps.enemies.search_subtitle',
                      "Recherche par nom d'ennemis"
                    )}
                  </label>
                  <LanguageSelector
                    value={language}
                    options={languageOptions}
                    langLabel={t('multichoix.steps.enemies.languages', 'Langues')}
                    onChange={handleLanguageChange}
                  />
                </div>
                {/* Search input - full width */}
                <div className="mb-3">
                  <TreeSearchInput
                    value={searchValue}
                    onChange={(val) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [enemyType]: val,
                      }))
                    }
                    placeholder={t(
                      'multichoix.steps.enemies.search_placeholder',
                      'Rechercher...'
                    )}
                    width="100%"
                  />
                </div>

                {/* Select all checkbox */}
                <div className="mb-3 pb-2 border-b border-gray-100">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) =>
                      handleSelectAll(enemyType, filteredTreeData, e.target.checked)
                    }
                  >
                    {t('multichoix.common.select_all', 'Sélectionner tout')}
                  </Checkbox>
                  {/* SP-430 M90: per-group « Sélectionner toutes les … » under
                      the global select-all, indented, coloured like their group
                      (weed-cb-<id> CSS). Checking « Sélectionner tout » above
                      auto-checks these (their state derives from the selection). */}
                  {enemyType === 'weeds' && visibleWeedGroups.length > 0 && (
                    <div className="ml-6 mt-1 flex flex-col gap-1 green-checkbox">
                      {[
                        ...visibleWeedGroups,
                        ...(hasUngroupedWeeds ? [{ id: WEED_NO_GROUP_VALUE, label: 'Autres' }] : []),
                      ].map((g) => (
                        <Checkbox
                          key={g.id}
                          className={`weed-cb-${g.id} text-xs`}
                          checked={isWeedGroupAllSelected(filteredTreeData, g.id)}
                          onChange={(e) =>
                            handleSelectWeedGroup(enemyType, filteredTreeData, g.id, e.target.checked)
                          }
                        >
                          <span className="text-xs">
                            {g.id === WEED_NO_GROUP_VALUE
                              ? 'Sélectionner toutes les autres mauvaises herbes'
                              : `Sélectionner toutes les ${(g.label || '').toLowerCase()}`}
                          </span>
                        </Checkbox>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tree */}
                <div className="max-h-[350px] overflow-y-auto">
                  {filteredTreeData.length > 0 ? (
                    <Tree
                      checkable
                      checkStrictly
                      checkedKeys={{
                        checked: typeCheckedKeys,
                        halfChecked: [],
                      }}
                      onCheck={(keys, info) =>
                        handleCheck(keys, info, enemyType, treeData)
                      }
                      expandedKeys={expandedKeys[enemyType] || []}
                      onExpand={(keys) => handleExpand(keys, enemyType)}
                      treeData={filteredTreeData}
                      // SP-430 M82: render the admin rich-text name (HtmlContent)
                      // when markup exists, else the plain title. M75: show the
                      // matched synonym in gray on the right (« Synonyme : X »).
                      titleRender={(node) => {
                        const html = getDisplayHtml(node.enemyData);
                        const nameEl = html ? (
                          <HtmlContent html={html} as="span" />
                        ) : (
                          node.title
                        );
                        return node.matchedSynonym ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                            <span>{nameEl}</span>
                            <span
                              style={{
                                color: '#8c8c8c',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                marginLeft: 'auto',
                                paddingLeft: 8,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Synonyme&nbsp;: {node.matchedSynonym}
                            </span>
                          </span>
                        ) : (
                          nameEl
                        );
                      }}
                      // SP-430 M66: clicking anywhere on the enemy name toggles
                      // its checkbox (not only the small box).
                      selectable
                      onSelect={(_keys, info) =>
                        handleCheck(
                          null,
                          { node: info.node, checked: !typeCheckedKeys.includes(info.node.key) },
                          enemyType,
                          treeData
                        )
                      }
                      blockNode
                      className="enemy-tree"
                    />
                  ) : (
                    <Empty
                      description={t(
                        'multichoix.steps.enemies.no_results',
                        'Aucun ennemi trouvé'
                      )}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </div>
              </div>

              {/* Right side - Selected elements (50%) */}
              <div className="flex-1 p-4">
                <fieldset className="border border-[#c4c49a] rounded min-h-[200px]">
                  <legend className="ml-2 px-2 text-sm text-gray-700 font-medium">
                    {t(
                      'multichoix.steps.enemies.selected_elements',
                      'Éléments sélectionnés:'
                    )}
                  </legend>
                  <div className="p-3">
                    {typeSelectedLabels.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {typeSelectedLabels.map((item) => {
                          // SP-430 M79: recompute the tag label LIVE from the
                          // current-language tree so already-selected enemies
                          // switch language with the « Langues » dropdown.
                          const node = findNodeByKey(treeData, item.value);
                          const displayLabel = node ? getDisplayName(node.enemyData) : item.label;
                          return (
                            <Tag
                              key={item.value}
                              closable
                              onClose={() => handleRemoveTag(item)}
                              className="bg-[#5b8c00] border-[#5b8c00] text-white px-2 py-0.5 text-sm"
                            >
                              {displayLabel}
                            </Tag>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">
                        {t(
                          'multichoix.steps.enemies.no_selection',
                          'Aucun élément sélectionné'
                        )}
                      </p>
                    )}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EnemiesStepContent;
